import { useMemo } from 'react';

import {
  ItemTypeCategory,
  type MerchantOrderTypeEnum,
} from '@/graphql/generatedTypes';
import { useAllItemTypesQuery } from '@/graphql/queries/allItemTypes.generated';
import { isOrderableStoreAssetItemType } from '@/utils/orderableStoreAssets';

import { orderTypeAllowsStoreAssets } from './storeAssetOrderTypes';

export interface StoreAssetOption {
  item_type_id: number;
  name: string;
  imageUrl: string | null;
  hexCode: string | null;
}

/**
 * The STORE_ASSET ItemTypes offerable on an order. Unlike containers and
 * untracked items these do not come from merchantOrderItemOptions — they are not
 * store portfolio — so the full list is fetched and filtered by the order type
 * gate and ORDERABLE_STORE_ASSET_ITEM_TYPE_IDS instead.
 */
export function useStoreAssetOptions({
  orderType,
  skip,
}: {
  orderType: MerchantOrderTypeEnum | null | undefined;
  skip?: boolean;
}): { options: StoreAssetOption[]; loading: boolean } {
  // Nothing to offer on an order type that cannot carry store assets, so no query.
  const allowed = orderTypeAllowsStoreAssets(orderType);

  const { data, loading } = useAllItemTypesQuery({
    variables: {
      filter: { category: ItemTypeCategory.StoreAsset, archived: false },
      take: 500,
    },
    skip: skip || !allowed,
    fetchPolicy: 'cache-first',
  });

  const options = useMemo<StoreAssetOption[]>(() => {
    if (!allowed) return [];
    return [...(data?.itemTypes?.items ?? [])]
      .filter((itemType) => isOrderableStoreAssetItemType(itemType.id))
      .map((itemType) => ({
        item_type_id: itemType.id,
        name: itemType.name ?? `#${itemType.id}`,
        imageUrl: itemType.image_url ?? null,
        hexCode: itemType.color_hex_code ?? null,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allowed, data]);

  return { options, loading };
}
